import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#removeShippingProtection();
  }

  async #removeShippingProtection() {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const protectionItem = cart.items.find((item) => item.properties?._shipping_protection === 'true');
      if (!protectionItem) return;

      await fetch('/cart/change.js', fetchConfig('json', {
        body: JSON.stringify({ id: protectionItem.key, quantity: 0 }),
      }));

      location.reload();
    } catch {
      // Silent fail — protection item may not exist
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line, cartLineKey: lineKey } = event.detail;

    if (!line && !lineKey) return;

    if (quantity === 0) {
      return this.onLineItemRemove({ line, key: lineKey });
    }

    this.updateQuantity({
      line,
      lineKey,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#findCartItemRow(line, lineKey);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number | {line?: number, key?: string}} lineOrData - The line item index or line item data.
   * @param {Event} [event] - The click event.
   */
  onLineItemRemove(lineOrData, event) {
    const line = typeof lineOrData === 'number' ? lineOrData : Number(lineOrData?.line || 0);
    let lineKey = typeof lineOrData === 'object' ? lineOrData.key : undefined;

    if (!lineKey && event?.target instanceof Element) {
      const sourceRow = event.target.closest('[data-key]');
      if (sourceRow instanceof HTMLElement) lineKey = sourceRow.dataset.key;
    }

    if (!line && !lineKey) return;

    this.updateQuantity({
      line,
      lineKey,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#findCartItemRow(line, lineKey);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line.
   * @param {string} [config.lineKey] - The stable Shopify line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, lineKey, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const bodyData = {
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (lineKey) {
      bodyData.id = lineKey;
    } else if (line) {
      bodyData.line = line;
    } else {
      this.#enableCartItems();
      return;
    }

    const body = JSON.stringify(bodyData);

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText, lineKey);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number | undefined} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   * @param {string | undefined} lineKey - The stable Shopify line item key.
   */
  #handleCartError = (line, parsedResponseText, lineKey) => {
    const lineItemRow = this.#findCartItemRow(line, lineKey);
    const quantitySelector =
      lineItemRow?.querySelector('.quantity-selector') || (line ? this.refs.quantitySelectors?.[line - 1] : undefined);
    const quantityInput = lineItemRow?.querySelector('input') || quantitySelector?.querySelector('input');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError =
      lineItemRow?.querySelector('.cart-item__error-text') || (line ? this.refs[`cartItemError-${line}`] : undefined);
    const cartItemErrorContainer =
      lineItemRow?.querySelector('.cart-items__error') ||
      (line ? this.refs[`cartItemErrorContainer-${line}`] : undefined);

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      console.warn('Cart item error target not found', parsedResponseText.errors);
      return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Finds a rendered cart item row by Shopify line key first, then by line number.
   * @param {number | undefined} line
   * @param {string | undefined} lineKey
   * @returns {HTMLElement | undefined}
   */
  #findCartItemRow(line, lineKey) {
    const rows = Array.isArray(this.refs.cartItemRows)
      ? this.refs.cartItemRows.filter((row) => row instanceof HTMLElement)
      : [];

    if (lineKey) {
      const rowByKey = rows.find((row) => row.dataset.key === lineKey);
      if (rowByKey) return rowByKey;
    }

    if (line) {
      return rows.find((row) => Number(row.dataset.cartLine) === line) || rows[line - 1];
    }

    return undefined;
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    // Skip morphing for CartAddEvents when inside a cart-drawer with auto-open,
    // BUT only if the add action came from OUTSIDE the drawer.
    // The cart-drawer handles the morph first to ensure content is updated before opening.
    // If adding from INSIDE the drawer (e.g., recommendations), we should still update.
    if (event instanceof CartAddEvent) {
      const parentDrawer = this.closest('cart-drawer-component');
      if (parentDrawer?.hasAttribute('auto-open')) {
        // Check if the event originated from within this drawer
        const eventFromInsideDrawer = event.target instanceof Node && parentDrawer.contains(event.target);
        if (!eventFromInsideDrawer) {
          return;
        }
      }
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
